import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';

import Comments from './Comments';
import YoutubeVideo from './YoutubeVideo';
import ImageLightbox from './ImageLightbox';
import { SiteHeader, SiteFooter } from './SiteLayout';
import { profileHeaders, hasProfileSession } from './profileAuth';
import { getHostedVideo, getImagePaths, getMemoryText, normalizeMemory, normalizeProfile, ROMAN_SLUG } from './memoryUtils';

import './MemoryDetailPage.css';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'long' }).format(date);
};

const MemoryDetailPage = () => {
  const { memoryId, profileSlug } = useParams();
  const backPath = profileSlug ? `/p/${profileSlug}` : '/mgr-Roman-Kalab-Riha-2024-30-11';
  const [memory, setMemory] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxMemory, setLightboxMemory] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadMemory = async () => {
      setLoading(true);
      setError('');
      try {
        const profileResponse = await axios.get(`/api/profiles/slug/${profileSlug || ROMAN_SLUG}`);
        const loadedProfile = normalizeProfile(profileResponse.data);
        const response = await axios.get('/api/memories', { params: { id: memoryId, profile_id: loadedProfile.id }, headers: profileHeaders(loadedProfile.id) });
        if (!response.data) {
          setError('Vzpomínka nebyla nalezena.');
          return;
        }
        if (!cancelled) {
          setProfile(loadedProfile);
          setMemory(normalizeMemory(response.data));
          document.title = `Vzpomínka | ${loadedProfile.name}`;
        }
      } catch {
        if (!cancelled) setError('Vzpomínku se nepodařilo načíst.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadMemory();

    return () => {
      cancelled = true;
    };
  }, [memoryId, profileSlug]);

  if (loading) {
    return (
      <main className="memory-detail-page">
        <div className="memory-detail-shell">Načítám vzpomínku...</div>
      </main>
    );
  }

  if (error || !memory) {
    return (
      <main className="memory-detail-page">
        <div className="memory-detail-shell">
          <Link to={backPath} className="memory-detail-back">
            Zpět na vzpomínky
          </Link>
          <p>{error || 'Vzpomínka nebyla nalezena.'}</p>
        </div>
      </main>
    );
  }

  const { primaryText, secondaryText } = getMemoryText(memory);
  const imagePaths = getImagePaths(memory);
  const imageSrc = imagePaths.reduced || imagePaths.original || imagePaths.thumbnail;
  const hostedVideo = getHostedVideo(memory);
  const typeLabel =
    {
      image: 'Fotografie',
      story: 'Příběh',
      quotation: 'Citát',
      youtube: 'Video na YouTube',
      video: 'Video',
    }[memory.metadata.type] || 'Vzpomínka';

  return (
    <>
      <SiteHeader />
      <main className="memory-detail-page">
        <div className="memory-detail-shell">
          <Link to={backPath} className="memory-detail-back">
            Zpět na vzpomínky · {profile.name}
          </Link>
          {hasProfileSession(profile.id) && (
            <Link className="memory-detail-admin" to={`/p/${profile.slug}/admin#memory-${memory.id}`}>
              Upravit vzpomínku
            </Link>
          )}

          <article className={`memory-detail memory-detail-${memory.metadata.type || 'unknown'}`}>
            <header className="memory-detail-header">
              <span>{typeLabel}</span>
              {memory.author && <p>{memory.author}</p>}
            </header>

            {imageSrc && (
              <button type="button" className="memory-detail-image-button" onClick={() => setLightboxMemory(memory)} aria-label="Zvětšit fotografii">
                <img src={imageSrc} alt={secondaryText || primaryText || 'Fotografie vzpomínky'} />
              </button>
            )}
            {memory.metadata.type === 'youtube' && <YoutubeVideo key={memory.id} url={memory.metadata.youtubeUrl} title={memory.shortDescription || 'Video na YouTube'} />}
            {hostedVideo && <video className="memory-hosted-player" src={hostedVideo.url} poster={hostedVideo.poster} controls playsInline preload="none" aria-label={memory.shortDescription || 'Video vzpomínky'} />}

            <div className="memory-detail-body">
              {secondaryText && <h1>{secondaryText}</h1>}
              {memory.metadata.type === 'quotation' ? primaryText && <blockquote>&quot;{primaryText}&quot;</blockquote> : primaryText && <p>{primaryText}</p>}
            </div>

            <footer className="memory-detail-footer">{formatDate(memory.dateOfInsert) && <span>Přidáno {formatDate(memory.dateOfInsert)}</span>}</footer>
          </article>

          <Comments memoryId={memory.id} profileId={profile.id} profileSettings={profile.settings} />
        </div>

        <ImageLightbox memory={lightboxMemory} onClose={() => setLightboxMemory(null)} />
      </main>
      <SiteFooter />
    </>
  );
};

export default MemoryDetailPage;
